/**
 * New Usage Credit Entry dialog (AddCreditDialogComponent) — EB-3883, renamed EB-4128.
 *
 * Opened from the "New Entry" button in UsageCreditComponent. Creates a single
 * usage credit for the current customer (POST /invoice/v1/credits).
 *
 * State machine (mirrors add-billing-dialog):
 *   READY -> SUBMITTING (Add disabled + spinner, double-submit guard)
 *         -> SUCCESS  (dialogRef.close(created Credit))
 *         |  ERROR    (error toast, stay open, back to READY)
 *
 * Fields:
 *  - Type: segmented toggle General (0) / Expired (2), default General, labelled
 *    "General (Allgemein)" / "Expired (Verfallen)" with sign icon + colour (EB-4128).
 *    No type-1 (BR-2).
 *  - Valid until: native date input + "No date" checkbox. Default depends on the
 *    type (EB-4128, product decision 2026-09-24): General → next invoicing day (the
 *    credit counts on the next invoice run; today if the invoicing day is unknown),
 *    Expired → today (the write-off applies now). Switching the type moves the date
 *    along until the operator edits it. KV opens the field on today for every type
 *    (`KundenBillingFrame` JDateChooser) — deliberate deviation. "No date" checked →
 *    date input disabled and `validUntil: null` posted (old KV parity, BR-4);
 *    unchecked again → back to the type's default.
 *  - "Set to invoicing day" (EB-4128, no KV equivalent): sets Valid until to the
 *    next invoicing day — old CMT `getDefaultEndDate` (`defaultEndDate`).
 *  - Amount (€): locale-decimal (comma or dot — reuses parseLocaleDecimal),
 *    required, must be ≠ 0. No sign normalization (operator enters the sign).
 *  - Description: optional, maxlength 255 + live counter (BR-6).
 *
 * `employeeId` is the logged-in agent's user id (authService.currentUserId,
 * UserDetails.userId) — sent on POST, never displayed (BR-4).
 */
import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../../shared/confirm-dialog/confirm-dialog.component';
import {
  HLM_DIALOG_SHELL,
  HLM_FORM_FIELD,
  HlmBtnDirective,
  HlmCheckboxComponent,
  HlmIconComponent,
  HlmInputDirective,
  HlmSpinnerComponent,
  HlmAlertComponent,
  ToastService,
} from '../../../../ui';
import { AuthService } from '../../../../core/auth/auth.service';
import { LoggingService } from '../../../../core/services/logging.service';
import { toLocalIsoDay, todayIso } from '../../../../shared/utils/iso-date.util';
import { defaultEndDate } from '../../../../shared/utils/invoice-day.util';
import { parseLocaleDecimal } from '../../customer-billing/add-billing-dialog/util/locale-decimal.util';
import { UsageCreditService } from '../usage-credit.service';
import {
  CreateCreditPayload,
  Credit,
  creditTypeLabel,
  CREDIT_TYPE_EXPIRED,
  CREDIT_TYPE_GENERAL,
} from '../usage-credit.models';
import {
  amountSignMatchesType,
  applySignToDecimalString,
  expectedSignForType,
  hasExplicitSign,
} from '../credit-sign.util';

/** Input contract — resolved from `MAT_DIALOG_DATA`. */
export interface AddCreditDialogData {
  customerId: number;
  /** Customer invoicing day (1–31); null/absent hides "Set to invoicing day". */
  invoiceDay?: number | null;
}

/** Dialog lifecycle states driving the UI. */
export type DialogState = 'READY' | 'CONFIRMING' | 'SUBMITTING';

/** Maximum reason length — server rejects longer (BR-6). */
const REASON_MAX_LENGTH = 255;

@Component({
  selector: 'cmt-add-credit-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DatePipe,
    ...HLM_DIALOG_SHELL,
    ...HLM_FORM_FIELD,
    HlmBtnDirective,
    HlmCheckboxComponent,
    HlmIconComponent,
    HlmInputDirective,
    HlmSpinnerComponent,
    HlmAlertComponent,
  ],
  templateUrl: './add-credit-dialog.component.html',
})
export class AddCreditDialogComponent {
  readonly dialogRef = inject(MatDialogRef<AddCreditDialogComponent, Credit | null>);
  readonly data: AddCreditDialogData = inject(MAT_DIALOG_DATA);

  private readonly fb = inject(FormBuilder);
  private readonly creditService = inject(UsageCreditService);
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly loggingService = inject(LoggingService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  /** Exposed for the template's radio values. */
  readonly TYPE_GENERAL = CREDIT_TYPE_GENERAL;
  readonly TYPE_EXPIRED = CREDIT_TYPE_EXPIRED;
  readonly reasonMaxLength = REASON_MAX_LENGTH;

  readonly form: FormGroup = this.fb.group({
    type: this.fb.control<number>(CREDIT_TYPE_GENERAL, { nonNullable: true }),
    validUntil: this.fb.control<string>('', { nonNullable: true }),
    noDate: this.fb.control<boolean>(false, { nonNullable: true }),
    amountInput: this.fb.control<string>('', {
      nonNullable: true,
      validators: [Validators.required, this.validNonZeroDecimal()],
    }),
    reason: this.fb.control<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(REASON_MAX_LENGTH)],
    }),
  });

  readonly state = signal<DialogState>('READY');
  readonly submitting = computed(() => this.state() === 'SUBMITTING');

  /** Mirror the reason length as a signal for the live counter. */
  private readonly reasonValueSig = toSignal(this.form.controls.reason.valueChanges, {
    initialValue: this.form.controls.reason.value,
  }) as () => string;
  readonly reasonLength = computed(() => (this.reasonValueSig() ?? '').length);

  /** Next invoicing day as `yyyy-MM-dd`, or null when the customer has none. */
  readonly invoicingDayIso = computed(() => {
    const invoiceDay = this.data.invoiceDay;
    return invoiceDay ? toLocalIsoDay(defaultEndDate(invoiceDay)) : null;
  });

  /** Mirror the "No date" checkbox so the template can disable the date input. */
  readonly noDateSig = toSignal(this.form.controls.noDate.valueChanges, {
    initialValue: this.form.controls.noDate.value,
  }) as () => boolean;

  constructor() {
    this.form.controls.validUntil.setValue(this.defaultValidUntil(CREDIT_TYPE_GENERAL));

    // "No date" toggles the date input enabled/disabled. When checked, the
    // posted validUntil is null (old KV parity).
    this.form.controls.noDate.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((noDate) => {
        const validUntil = this.form.controls.validUntil;
        if (noDate) {
          validUntil.setValue('', { emitEvent: false });
          validUntil.disable({ emitEvent: false });
        } else {
          validUntil.enable({ emitEvent: false });
          if (!validUntil.value) validUntil.setValue(this.defaultValidUntil(this.form.controls.type.value), { emitEvent: false });
        }
      });

    // Proactively set the type's conventional sign on the amount when the type
    // changes (General +, Expired −). Flips an existing magnitude; re-asserts the
    // type sign over any prior explicit sign. A deliberate opposite sign can be
    // re-entered afterwards and is confirmed on submit.
    this.form.controls.type.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((type) => {
        const amount = this.form.controls.amountInput;
        const signed = applySignToDecimalString(amount.value ?? '', expectedSignForType(type ?? CREDIT_TYPE_GENERAL));
        if (signed !== amount.value) amount.setValue(signed, { emitEvent: false });

        // The date follows the type's default until the operator picks one.
        const validUntil = this.form.controls.validUntil;
        if (!validUntil.dirty && !this.form.controls.noDate.value) {
          validUntil.setValue(this.defaultValidUntil(type ?? CREDIT_TYPE_GENERAL), { emitEvent: false });
        }
      });
  }

  /** General → next invoicing day (fallback today); Expired → today (EB-4128). */
  private defaultValidUntil(type: number): string {
    return type === CREDIT_TYPE_GENERAL ? (this.invoicingDayIso() ?? todayIso()) : todayIso();
  }

  /**
   * On amount blur, normalize a sign-less entry to the type's conventional sign
   * (so "25" under Expired becomes "−25" without the operator typing the minus).
   * An explicitly-signed entry is left as typed — a deliberate deviation, caught
   * by the submit confirm.
   */
  onAmountBlur(): void {
    const amount = this.form.controls.amountInput;
    const raw = amount.value ?? '';
    if (raw.trim().length === 0 || hasExplicitSign(raw)) return;
    const signed = applySignToDecimalString(raw, expectedSignForType(this.form.controls.type.value ?? CREDIT_TYPE_GENERAL));
    if (signed !== raw) amount.setValue(signed, { emitEvent: false });
  }

  /** "Set to invoicing day" — Valid until = next invoicing day (EB-4128). */
  setToInvoicingDay(): void {
    const invoicingDay = this.invoicingDayIso();
    if (!invoicingDay || this.form.controls.noDate.value) return;
    const validUntil = this.form.controls.validUntil;
    validUntil.setValue(invoicingDay);
    validUntil.markAsDirty();
  }

  /** Validator: input parses to a finite, non-zero decimal (comma or dot). */
  private validNonZeroDecimal(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value ?? '') as string;
      if (value.trim().length === 0) return null; // Validators.required owns emptiness
      const parsed = parseLocaleDecimal(value);
      if (Number.isNaN(parsed)) return { localeDecimal: true };
      if (parsed === 0) return { zeroAmount: true };
      return null;
    };
  }

  submit(): void {
    if (this.state() !== 'READY') return; // guard re-entry (SUBMITTING or confirm pending)
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const amount = parseLocaleDecimal(raw.amountInput ?? '');
    const type = raw.type ?? CREDIT_TYPE_GENERAL;
    const noDate = raw.noDate === true;
    const validUntil = noDate ? null : (raw.validUntil || null);

    const payload: CreateCreditPayload = {
      customerId: String(this.data.customerId),
      type,
      amount,
      validUntil,
      reason: (raw.reason ?? '').trim(),
      employeeId: this.authService.currentUserId,
    };

    // Amount matches the type's conventional sign → post directly. Otherwise
    // confirm the deliberate deviation before posting (old-KV freedom preserved).
    if (amountSignMatchesType(type, amount)) {
      this.performCreate(payload);
      return;
    }

    // Lock re-entry while the confirm is open (else a re-triggered submit could
    // stack confirms → duplicate POSTs).
    this.state.set('CONFIRMING');
    this.dialog
      .open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, {
        data: this.signDeviationDialogData(type),
        panelClass: 'cmt-hlm-dialog',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) this.performCreate(payload);
        else this.state.set('READY');
      });
  }

  /** POST the credit and resolve the dialog. */
  private performCreate(payload: CreateCreditPayload): void {
    this.state.set('SUBMITTING');
    this.creditService
      .createCredit(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // API returns 200 with the created entity — any 2xx is success.
        next: (created) => {
          /** @ticket EB-3798 — usage: a usage credit was successfully created (D9). */
          this.loggingService.usage('action.invoke', { action: 'add_credit', outcome: 'success' });
          this.dialogRef.close(created);
        },
        error: (err: { error?: { message?: string } }) => {
          const msg = err?.error?.message ?? 'Could not save the credit. Please try again.';
          this.toast.open(msg, 'OK', { duration: 5000, tone: 'error' });
          this.state.set('READY');
        },
      });
  }

  /** Type-specific copy for the sign-deviation confirm dialog. */
  private signDeviationDialogData(type: number): ConfirmDialogData {
    const label = creditTypeLabel(type);
    const message =
      expectedSignForType(type) < 0
        ? `This ${label} credit has a positive amount. Expired credits are normally negative. Create it anyway?`
        : `This ${label} credit has a negative amount. General credits are normally positive. Create it anyway?`;
    return { title: 'Unusual amount sign', message, confirmLabel: 'Create anyway' };
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
